import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { leadsApi } from '../../api/leads.js';
import { areasApi } from '../../api/areas.js';
import { usersApi } from '../../api/users.js';
import { crmApiInstance } from '../../api/client.js';

// Map a backend lead object to the shape this component uses
function mapLead(lead) {
  const salesRep = lead.SalesRep || lead.salesRep || null;
  const roleName = salesRep?.role?.name || '';
  const isTL = roleName.toLowerCase().includes('tl') || roleName.toLowerCase().includes('teamlead') || roleName.toLowerCase().includes('team_lead') || roleName.toLowerCase().includes('sales_tl');
  const areaObj = lead.area || lead.Area || null;
  const parentName = areaObj?.parent?.name || areaObj?.name;
  return {
    id: lead.id,
    name: lead.name || '',
    owner: lead.email || '',
    phone: lead.phone || '',
    zone: parentName || lead.source || '',
    area_id: lead.area_id || '',
    assigned: lead.Assignee?.name || (lead.assigned_to ? `User #${lead.assigned_to}` : ''),
    crmPersonId: lead.Assignee?.id ? String(lead.Assignee.id) : (lead.assigned_to ? String(lead.assigned_to) : ''),
    salesRep: salesRep ? `${salesRep.name}${isTL ? ' (TL)' : ''}` : '—',
    salesRepId: salesRep?.id || null,
    status: lead.is_active === false ? 'Inactive' : 'Active',
    _raw: lead,
  };
}

function AsyncSearchSelect({ value, onChange, placeholder, disabled, loadOptions, initialLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selLabel, setSelLabel] = useState(initialLabel || '');
  const wrapRef = useRef(null);
  const debRef = useRef(null);

  useEffect(() => { if (initialLabel) setSelLabel(initialLabel); }, [initialLabel]);
  useEffect(() => { if (!value) setSelLabel(''); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const list = await loadOptions(query);
        setOptions(list);
        if (value) {
          const found = list.find(o => o.value?.toString() === value?.toString());
          if (found) setSelLabel(found.label);
        }
      } catch (_) { setOptions([]); }
      finally { setLoading(false); }
    }, query ? 300 : 0);
    return () => clearTimeout(debRef.current);
  }, [query, isOpen]);

  const displayLabel = options.find(o => o.value?.toString() === value?.toString())?.label || selLabel;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        className="form-control"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: disabled ? 'not-allowed' : 'pointer', background: disabled ? '#f9fafb' : '', userSelect: 'none' }}
        onClick={() => !disabled && setIsOpen(o => !o)}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: displayLabel ? 'inherit' : '#9ca3af', fontSize: 13 }}>
          {displayLabel || placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {value && !disabled && (
            <span style={{ color: '#9ca3af', fontSize: 11, cursor: 'pointer' }}
              onClick={e => { e.stopPropagation(); onChange(''); setSelLabel(''); setQuery(''); }}>✕</span>
          )}
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ color: '#9ca3af' }}>
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {isOpen && !disabled && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
          <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid #f1f5f9' }}>
            <input autoFocus type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Type to search…"
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', boxSizing: 'border-box' }}
              onClick={e => e.stopPropagation()} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', padding: '4px 0' }}>
            {loading ? (
              <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            ) : options.length === 0 ? (
              <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>No results found</div>
            ) : options.map(opt => (
              <div key={opt.value}
                onClick={() => { onChange(opt.value); setSelLabel(opt.label); setIsOpen(false); setQuery(''); }}
                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, background: opt.value?.toString() === value?.toString() ? '#eff6ff' : 'transparent', color: opt.value?.toString() === value?.toString() ? '#1d4ed8' : 'inherit' }}
                onMouseEnter={e => { if (opt.value?.toString() !== value?.toString()) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = opt.value?.toString() === value?.toString() ? '#eff6ff' : 'transparent'; }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerModal({ customer, onClose, onSave }) {
  const [form, setForm] = useState(
    customer
      ? {
        name: customer.name, email: customer.owner || '', phone: customer.phone,
        parentAreaId: '', area_id: customer.area_id ? String(customer.area_id) : '',
        shopName: customer._raw?.shop_name || customer.name || '', gst: customer._raw?.gst_number || '',
        type: customer._raw?.customer_type || 'Retail', category: customer._raw?.customer_category || 'general', notes: customer._raw?.notes || '',
        address: customer._raw?.address || customer._raw?.address_line_1 || '',
        orgId: customer._raw?.organisation_id ? String(customer._raw.organisation_id) : '',
        status: customer.status,
        salesPersonId: customer.salesRepId ? String(customer.salesRepId) : '',
        crmPersonId: customer.crmPersonId || '',
      }
      : {
        name: '', email: '', phone: '',
        parentAreaId: '', area_id: '',
        shopName: '', gst: '', type: 'Retail', category: 'general', notes: '', address: '',
        orgId: '', status: 'Active', salesPersonId: '', crmPersonId: '',
      }
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Pre-populate parentAreaId when editing a customer that has an area
  useEffect(() => {
    if (!customer?.area_id) return;
    areasApi.getById(customer.area_id)
      .then(res => {
        const area = res.data ?? res;
        if (area.parent_id) {
          setForm(f => ({ ...f, parentAreaId: String(area.parent_id) }));
        } else {
          // top-level area — treat it as the parent
          setForm(f => ({ ...f, parentAreaId: String(area.id), area_id: '' }));
        }
      })
      .catch(() => { });
  }, []);

  const hc = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Reset child when parent area changes
  const prevParent = useRef(form.parentAreaId);
  useEffect(() => {
    if (prevParent.current !== form.parentAreaId) {
      prevParent.current = form.parentAreaId;
      setForm(f => ({ ...f, area_id: '', salesPersonId: '' }));
    }
  }, [form.parentAreaId]);

  // Reset sales person when area changes
  const prevArea = useRef(form.area_id);
  useEffect(() => {
    if (prevArea.current !== form.area_id) {
      prevArea.current = form.area_id;
      setForm(f => ({ ...f, salesPersonId: '' }));
    }
  }, [form.area_id]);

  /* ── Loaders ── */
  const loadOrgs = async (search) => {
    const res = await crmApiInstance.get('/leads/organisations', { params: { limit: 20, sortBy: 'name', sortOrder: 'ASC', ...(search ? { search } : {}) } });
    return (res.data?.data || res.data?.rows || []).map(o => ({ value: String(o.id), label: o.name }));
  };

  const loadParentAreas = async (search) => {
    const res = await areasApi.getAll({ parent_id: 'null', limit: 20, ...(search ? { search } : {}) });
    const d = res.data ?? res;
    return (Array.isArray(d) ? d : d.data ?? []).map(a => ({ value: String(a.id), label: a.name }));
  };

  const loadAreas = async (search) => {
    if (!form.parentAreaId) return [];
    const res = await areasApi.getAll({ parent_id: form.parentAreaId, limit: 20, ...(search ? { search } : {}) });
    const d = res.data ?? res;
    return (Array.isArray(d) ? d : d.data ?? []).map(a => ({ value: String(a.id), label: a.name }));
  };

  const loadStaffs = async (search) => {
    const effectiveArea = form.area_id || form.parentAreaId;
    if (!effectiveArea) return [];
    const res = await usersApi.getAll({ area_id: effectiveArea, limit: 20, ...(search ? { search } : {}) });
    const d = res.data ?? res;
    return (Array.isArray(d) ? d : d.data ?? []).map(s => ({ value: String(s.id), label: s.name }));
  };

  const loadCrmUsers = async (search) => {
    const res = await crmApiInstance.get('/admin/crm-users', { params: search ? { search } : {} });
    const d = res.data?.data || res.data || [];
    return (Array.isArray(d) ? d : []).map(u => ({ value: String(u.id), label: u.name }));
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { setErr('Full Name is required.'); return; }
    if (form.email && !form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { setErr('Valid email required.'); return; }
    if (!form.phone?.match(/^[6-9]\d{9}$/)) { setErr('Valid 10-digit phone required.'); return; }
    if (!form.shopName?.trim()) { setErr('Shop / Company Name is required.'); return; }
    if (!form.address?.trim()) { setErr('Address is required.'); return; }
    if (form.gst && !form.gst.match(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/)) { setErr('Invalid GST format.'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); }
    catch (e) { setErr(e.message || 'Save failed.'); setSaving(false); }
  };

  const SECTION = { marginBottom: 10, fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '640px', maxWidth: '92vw', maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{customer ? 'Edit Customer' : 'Add Customer'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>{err}</div>}

          <div style={SECTION}>Personal Information</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-control" value={form.name} onChange={e => hc('name', e.target.value)} placeholder="Enter full name" />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-control" value={form.email} onChange={e => hc('email', e.target.value)} placeholder="email@example.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <input className="form-control" value={form.phone} onChange={e => hc('phone', e.target.value)} placeholder="10-digit mobile number" maxLength={10} />
            </div>
            <div className="form-group">
              <label className="form-label">Customer Type</label>
              <select className="form-control" value={form.type} onChange={e => hc('type', e.target.value)}>
                {['Retail', 'Wholesale', 'Distributor', 'Corporate'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Customer Category</label>
              <select className="form-control" value={form.category} onChange={e => hc('category', e.target.value)}>
                {[['general', 'General Customer'], ['ecommerce', 'Ecommerce'], ['tender', 'Tender']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          <div style={SECTION}>Business Information</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Shop / Company Name *</label>
              <input className="form-control" value={form.shopName} onChange={e => hc('shopName', e.target.value)} placeholder="Enter shop or company name" />
            </div>
            <div className="form-group">
              <label className="form-label">GST Number</label>
              <input className="form-control" value={form.gst} onChange={e => hc('gst', e.target.value.toUpperCase())} placeholder="29ABCDE1234F1Z5" maxLength={15} />
            </div>
            <div className="form-group">
              <label className="form-label">Organisation</label>
              <AsyncSearchSelect value={form.orgId} onChange={v => hc('orgId', v)} placeholder="— Search organisation —" loadOptions={loadOrgs} />
            </div>
            <div className="form-group">
              <label className="form-label">Region / Parent Area</label>
              <AsyncSearchSelect value={form.parentAreaId} onChange={v => hc('parentAreaId', v)} placeholder="— Search region —" loadOptions={loadParentAreas} />
            </div>
            <div className="form-group">
              <label className="form-label">Area</label>
              <AsyncSearchSelect
                key={`area-${form.parentAreaId}`}
                value={form.area_id}
                onChange={v => hc('area_id', v)}
                placeholder={form.parentAreaId ? '— Search area —' : '— Select Region first —'}
                disabled={!form.parentAreaId}
                loadOptions={loadAreas}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sales Person</label>
              <AsyncSearchSelect
                key={`staff-${form.area_id || form.parentAreaId}`}
                value={form.salesPersonId}
                onChange={v => hc('salesPersonId', v)}
                placeholder="— Search sales person —"
                loadOptions={loadStaffs}
              />
            </div>
            <div className="form-group">
              <label className="form-label">CRM Person</label>
              <AsyncSearchSelect
                value={form.crmPersonId}
                onChange={v => hc('crmPersonId', v)}
                placeholder="— Search CRM user —"
                initialLabel={customer?.assigned || ''}
                loadOptions={loadCrmUsers}
              />
            </div>
          </div>

          <div style={SECTION}>Address &amp; Notes</div>
          <div style={{ display: 'grid', gap: 16, marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Full Address *</label>
              <textarea className="form-control" value={form.address} onChange={e => hc('address', e.target.value)} placeholder="Enter complete address with city and PIN code" rows={2} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" value={form.notes} onChange={e => hc('notes', e.target.value)} placeholder="Any special instructions or notes…" rows={2} />
            </div>
            <div className="form-group" style={{ maxWidth: 200 }}>
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => hc('status', e.target.value)}>
                <option>Active</option><option>Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Customer'}</button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: 13, alignItems: 'flex-start' }}>
      <span style={{ flexShrink: 0, fontSize: 15 }}>{icon}</span>
      <span style={{ color: 'var(--text-muted)', minWidth: 90, flexShrink: 0, paddingTop: 1 }}>{label}</span>
      <span style={{ fontWeight: 500, lineHeight: 1.5 }}>{value}</span>
    </div>
  );
}

const STATUS_FLAG_CFG = {
  'green ++': { label: 'Premium', bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
  'green': { label: 'Good', bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' },
  'yellow': { label: 'Average', bg: '#fef9c3', color: '#a16207', border: '#fde68a' },
  'red': { label: 'At Risk', bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
};

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}



export default function CustomersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFlag, setStatusFlag] = useState('');      // '' = All
  const [orderRecency, setOrderRecency] = useState(''); // '' = Any Order Date
  const [accountStatus, setAccountStatus] = useState(''); // '' = All, 'true' = Active, 'false' = Inactive
  const [createdFrom, setCreatedFrom] = useState(searchParams.get('created_from') || '');
  const [createdTo, setCreatedTo] = useState(searchParams.get('created_to') || '');
  const [statusCounts, setStatusCounts] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const perPage = 100;

  const hasActiveFilters = !!(search || statusFlag || orderRecency || accountStatus || createdFrom || createdTo);
  const handleClearFilters = () => {
    setSearch('');
    setStatusFlag('');
    setOrderRecency('');
    setAccountStatus('');
    setCreatedFrom('');
    setCreatedTo('');
    setSearchParams(p => { const n = new URLSearchParams(p); n.delete('created_from'); n.delete('created_to'); return n; });
    setPage(1);
  };

  // Fetch status flag counts on mount
  useEffect(() => {
    leadsApi.getStatusFlags()
      .then(res => {
        const arr = res.data ?? [];
        const counts = {};
        let total = 0;
        arr.forEach(r => { counts[r.status_flag] = r.count; total += r.count; });
        counts._total = total;
        setStatusCounts(counts);
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: perPage, page, search };
        if (statusFlag) params.status_flag = statusFlag;
        if (orderRecency) params.aging = orderRecency;
        if (accountStatus !== '') params.is_active = accountStatus;
        if (createdFrom) params.created_from = createdFrom;
        if (createdTo) params.created_to = createdTo;
        const res = await leadsApi.getAll(params);
        const data = res.data ?? res;
        const leads = data.data ?? data;
        setCustomers(Array.isArray(leads) ? leads.map(mapLead) : []);
        setTotalCount(res.count ?? res.data?.count ?? leads.length);
      } catch (err) {
        console.error('Failed to fetch leads:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [page, search, statusFlag, orderRecency, accountStatus, createdFrom, createdTo]);

  const paginated = customers;
  const totalPages = Math.ceil(totalCount / perPage);

  const handleSave = async (form) => {
    const payload = {
      name: form.name,
      email: form.email || undefined,
      phone: form.phone || undefined,
      shop_name: form.shopName,
      gst_number: form.gst || "",
      customer_type: form.type || "Retail",
      customer_category: form.category || "general",
      notes: form.notes || "",
      address: form.address || "",
      area_id: form.area_id || form.parentAreaId || undefined,
      status_flag: "yellow",
      is_active: form.status === 'Active',
      ...(form.orgId ? { organisation_id: parseInt(form.orgId, 10) } : {}),
      ...(form.salesPersonId ? { sales_id: parseInt(form.salesPersonId, 10) } : {}),
      ...(form.crmPersonId ? { assigned_to: parseInt(form.crmPersonId, 10) } : { assigned_to: null }),
    };

    if (editCustomer) {
      const res = await leadsApi.update(editCustomer.id, payload);
      const updated = mapLead(res.data ?? res);
      setCustomers(cs => cs.map(c => c.id === editCustomer.id ? updated : c));
    } else {
      const res = await leadsApi.create(payload);
      const created = mapLead(res.data ?? res);
      setCustomers(cs => [created, ...cs]);
    }
    setShowModal(false);
    setEditCustomer(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await leadsApi.remove(id);
      setCustomers(cs => cs.filter(c => c.id !== id));
    } catch (err) {
      alert(err.message || 'Delete failed.');
    }
  };

  return (
    <div className="page-body">
      {showModal && (
        <CustomerModal
          customer={editCustomer}
          onClose={() => { setShowModal(false); setEditCustomer(null); }}
          onSave={handleSave}
        />
      )}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Customers / Leads</span>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditCustomer(null); setShowModal(true); }}>
            + Add Customer
          </button>
        </div>
        <div className="card-body">
          {/* Search + filter dropdowns in one row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            <div className="search-bar" style={{ flex: '0 1 200px', minWidth: 120, height: 32 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input placeholder="Search…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>

            <select
              value={statusFlag}
              onChange={e => { setStatusFlag(e.target.value); setPage(1); }}
              style={{ fontSize: 12.5, padding: '4px 8px', borderRadius: 7, border: '1.5px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontFamily: 'inherit', height: 32, minWidth: 140, fontWeight: 500 }}
            >
              <option value="">All Status Flags</option>
              <option value="green ++">Green++ {statusCounts['green ++'] != null ? `(${statusCounts['green ++']})` : ''}</option>
              <option value="green">Green {statusCounts['green'] != null ? `(${statusCounts['green']})` : ''}</option>
              <option value="yellow">Yellow {statusCounts['yellow'] != null ? `(${statusCounts['yellow']})` : ''}</option>
              <option value="red">Red {statusCounts['red'] != null ? `(${statusCounts['red']})` : ''}</option>
            </select>

            <select
              value={orderRecency}
              onChange={e => { setOrderRecency(e.target.value); setPage(1); }}
              style={{ fontSize: 12.5, padding: '4px 8px', borderRadius: 7, border: '1.5px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontFamily: 'inherit', height: 32, minWidth: 160, fontWeight: 500 }}
            >
              <option value="">Any Order Date</option>
              <option value="1">Ordered &lt; 1 month ago</option>
              <option value="2">Ordered 1–2 months ago</option>
              <option value="3">Ordered 2–3 months ago</option>
              <option value="3+">Inactive &gt; 3 months / Never</option>
            </select>

             <select
              value={accountStatus}
              onChange={e => { setAccountStatus(e.target.value); setPage(1); }}
              style={{ fontSize: 12.5, padding: '4px 8px', borderRadius: 7, border: '1.5px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontFamily: 'inherit', height: 32, minWidth: 120, fontWeight: 500 }}
            >
              <option value="">All Accounts</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {(createdFrom || createdTo) && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(37,99,235,0.08)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.25)', padding: '3px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, height: 32, boxSizing: 'border-box' }}>
                Created: {fmtDate(createdFrom) || '…'} – {fmtDate(createdTo) || '…'}
                <button
                  onClick={() => {
                    setCreatedFrom(''); setCreatedTo(''); setPage(1);
                    setSearchParams(p => { const n = new URLSearchParams(p); n.delete('created_from'); n.delete('created_to'); return n; });
                  }}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                >
                  ✕
                </button>
              </div>
            )}

            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="btn btn-outline btn-sm"
                style={{ height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, padding: '0 12px', borderRadius: 7, fontWeight: 500, borderColor: 'var(--border)' }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading customers…</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '5%' }}>Sr. No.</th>
                    <th style={{ width: '35%' }}>Customer Name</th>
                    <th style={{ width: '15%' }}>Phone</th>
                    <th style={{ width: '20%' }}>Area / Source</th>
                    <th style={{ width: '15%' }}>CRM Person</th>
                    <th style={{ width: '10%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '28px', color: 'var(--text-muted)' }}>
                        No customers found
                      </td>
                    </tr>
                  ) : customers.map((c, idx) => (
                    <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)} style={{ cursor: 'pointer' }}>
                      <td style={{ color: 'var(--text-muted)', fontWeight: 500, fontSize: 11.5 }}>
                        {(page - 1) * perPage + idx + 1}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 12.5 }}>
                            {c.name}
                          </span>
                        </div>
                      </td>
                      <td>{c.phone || '—'}</td>
                      <td>{c.zone || '—'}</td>
                      <td>{c.assigned || '—'}</td>
                      {/* <td>
                        {c.salesRep !== '—' ? (
                          <span style={{ fontWeight: 600, color: c.salesRep.includes('(TL)') ? 'var(--primary)' : 'inherit' }}>
                            {c.salesRep}
                          </span>
                        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td> */}
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-primary btn-xs" onClick={(e) => { e.stopPropagation(); navigate(`/place-order?lead_id=${c.id}`); }}>Order</button>
                          <button className="btn btn-outline btn-xs" onClick={(e) => { e.stopPropagation(); setEditCustomer(c); setShowModal(true); }}>Edit</button>

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && totalCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 24, gap: 16 }}>
              <button
                className="btn btn-outline"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span style={{ fontSize: 14, color: '#475569', fontWeight: 500 }}>
                Page {page} of {totalPages} (Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, totalCount)} of {totalCount} entries)
              </span>
              <button
                className="btn btn-outline"
                disabled={page === totalPages || totalPages === 0}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

